import React, { FC, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import { faImage } from '@fortawesome/pro-solid-svg-icons/faImage';
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash';
import { Alert, Field, Icon, IconButton } from '@kontentino/ui';
import useVideoThumbnailUploader from 'app/modules/posts/hooks/useVideoThumbnailUploader';
import { useMutation } from 'react-query';
import { MediaApi } from 'api/media';
import useSetVideoThumbnailPostComposerForm from 'app/modules/posts/hooks/useSetVideoThumbnailPostComposerForm';
import { VideoThumbnail } from 'types/MediaLibrary';
import { PostComposerFormState } from 'app/modules/posts/modules/post/components/composer/utils/postComposerFormState';
import DropZone from 'app/components/fileSourceInput/dropZone';

type Props = {
  value: PostComposerFormState;
};

const PostComposerContentFacebookVideoThumbnail: FC<Props> = ({ value }) => {
  const { t } = useTranslation();
  const uploader = useVideoThumbnailUploader({
    videoId: value.attachments.cards[0]?.medium?.id,
    onMediaUploaded: onUploaded,
  });

  const inputProps = uploader.dropzone.getInputProps();

  const { mutate: removeCustomThumbnail } = useMutation(
    MediaApi.removeVideoThumbnail,
  );
  const setCustomThumbnail = useSetVideoThumbnailPostComposerForm();

  const [thumbnail, setThumbnail] = useState<VideoThumbnail | null>(
    value.attachments.cards[0]?.medium?.type === 'video'
      ? value.attachments.cards[0]?.medium?.custom_thumb
      : null,
  );

  function onUploaded(thumbnail: VideoThumbnail) {
    setThumbnail(thumbnail);
    setCustomThumbnail({
      id: thumbnail.id,
      src: thumbnail.src,
      width: thumbnail.width,
      height: thumbnail.height,
    });
  }

  function onDelete() {
    setThumbnail(null);
    setCustomThumbnail(null);

    if (value.attachments.cards[0]?.medium?.id) {
      removeCustomThumbnail(value.attachments.cards[0].medium.id);
    }
  }

  return (
    <Field.Group>
      <Field.Label required>Custom thumbnail</Field.Label>
      {uploader.isError && (
        <div className="tw-mb-4">
          <Alert variant="danger">The upload of your file failed</Alert>
        </div>
      )}
      {!thumbnail && !uploader.isLoading && (
        <>
          <input {...inputProps} />
          <DropZone
            {...uploader.dropzone.getRootProps()}
            specifications={{
              accept: inputProps.accept,
              multiple: inputProps.multiple,
            }}
            fileSources={[
              {
                label: t('fromDevice'),
                onClick: uploader.dropzone.open,
                attributes: {
                  dataName: 'file-source_dropzone',
                  dataCy: 'file-source_dropzone',
                },
              },
            ]}
            isDragActive={uploader.dropzone.isDragActive}
          >
            <DropZone.DropZoneHelperText />
            <DropZone.DropZoneFileSelect />
            <DropZone.DropZoneActiveDragOverlay />
          </DropZone>
        </>
      )}
      {uploader.isLoading && <Skeleton height={46} />}
      {thumbnail && (
        <div className="tw-flex tw-items-center tw-rounded-sm tw-border tw-border-solid tw-border-grayscale-30 tw-p-2">
          <Icon icon={faImage} className="tw-mr-3 tw-text-grayscale-100" />
          <span className="tw-text-md tw-font-medium">
            Custom thumbnail image
          </span>
          <IconButton
            icon={<Icon icon={faTrash} />}
            size="small"
            style={{ marginLeft: 'auto' }}
            variant="secondary"
            onClick={onDelete}
          />
        </div>
      )}
    </Field.Group>
  );
};

export default PostComposerContentFacebookVideoThumbnail;
